import type { DragItem, Key } from 'react-aria';

import type * as api from '@gitbook/api-client';
import { type DragAndDropOptions, useDragAndDrop } from '@gitbook/spine-react';
import { assert, assertNever } from '@gitbook/util-ts';

import { addSectionsToGroup } from './addSectionsToGroup';
import { moveSiteExternalLink } from './useMoveSiteExternalLink';
import {
    getSiteSectionGroupMoveDestinations,
    moveSiteSectionGroup,
} from './useMoveSiteSectionGroup';

type DraggableSiteItem =
    | api.SiteSection
    | api.SiteSectionGroup
    | api.SiteSpace
    | api.SiteExternalLink;

export interface UseSiteStructureTreeDragAndDropProps extends Pick<
    DragAndDropOptions,
    'onDragMove' | 'onDragEnd'
> {
    getItem: (key: Key) => DraggableSiteItem | null;
    getItemIndex: (key: Key) => number;
    checkIsSectionInGroup: (siteSection: api.SiteSection) => boolean;
    draggedKeys: Set<Key>;
    setDraggedKeys(keys: Set<Key>): void;
    expandedKeys: Set<Key>;
    setExpandedKeys(keys: Set<Key>): void;
    organization: api.Organization;
    site: api.Site;
    siteStructure: api.SiteStructure;
    nestedSectionGroupsEnabled: boolean;
    onReorder: (props: {
        movedItem: DraggableSiteItem;
        targetItem: DraggableSiteItem;
        position: 'before' | 'after';
    }) => void;
    isDisabled?: boolean;
}

/**
 * Drag and drop operations hook for site structure reordering
 */
export function useSiteStructureTreeDragAndDrop(props: UseSiteStructureTreeDragAndDropProps) {
    const {
        getItem,
        getItemIndex,
        checkIsSectionInGroup,
        draggedKeys,
        setDraggedKeys,
        expandedKeys,
        setExpandedKeys,
        organization,
        site,
        siteStructure,
        nestedSectionGroupsEnabled,
        onReorder,
        isDisabled,
        onDragMove,
        onDragEnd,
    } = props;
    return useDragAndDrop({
        isDisabled,
        onDragStart: (event) => {
            setDraggedKeys(event.keys);
        },
        onDragMove,
        onDragEnd: (e) => {
            setDraggedKeys(new Set());
            onDragEnd?.(e);
        },
        // Open the item when we drag over it.
        onDropActivate: (event) => {
            // If we drop on an item, select it.
            if (event.target.type === 'item' && event.target.dropPosition === 'on') {
                // If not already expanded.
                if (!expandedKeys.has(event.target.key)) {
                    setExpandedKeys(new Set(expandedKeys).add(event.target.key));
                }
            }
        },
        getItems: (keys) => {
            return Array.from(keys).map((key) => {
                const item = getItem(key);
                assert(item, `Item with key ${key} not found`);
                const data: DragItem = {
                    [`x-gitbook/${item.object}`]: String(item.id),
                    'text/plain': item.title,
                };
                return data;
            });
        },
        getAllowedDropOperations: () => ['move'],
        acceptedDragTypes: [
            'x-gitbook/site-section-group',
            'x-gitbook/site-section',
            'x-gitbook/site-space',
            'x-gitbook/site-external-link',
        ],
        getDropOperation(target, _types) {
            const draggedItems = Array.from(draggedKeys)
                .map((key) => getItem(key))
                .filter((x) => x !== null);

            // No valid items dragged, cancel the operation.
            if (draggedItems.length === 0) {
                return 'cancel';
            }

            // We only support dropping on items.
            if (target.type !== 'item') {
                return 'cancel';
            }

            // If we want to drag just after an expanded item, cancel the operation.
            if (target.dropPosition === 'after' && expandedKeys.has(target.key)) {
                return 'cancel';
            }

            const targetItem = getItem(target.key);

            // Not a valid target, cancel the operation.
            if (!targetItem) {
                return 'cancel';
            }

            if (target.dropPosition === 'on') {
                // Only allow dropping on site section groups.
                if (targetItem.object !== 'site-section-group') {
                    return 'cancel';
                }

                const allSections = draggedItems.every((item) => item.object === 'site-section');
                const singleExternalLink =
                    draggedItems.length === 1 && draggedItems[0]?.object === 'site-external-link';
                const singleSectionGroup =
                    draggedItems.length === 1 && draggedItems[0]?.object === 'site-section-group'
                        ? draggedItems[0]
                        : null;
                if (!allSections && !singleExternalLink && !singleSectionGroup) {
                    return 'cancel';
                }

                if (singleSectionGroup) {
                    const canMoveToTarget = getSiteSectionGroupMoveDestinations({
                        siteSectionGroup: singleSectionGroup,
                        siteStructure,
                        nestedSectionGroupsEnabled,
                    }).some((destination) => destination?.id === targetItem.id);
                    return canMoveToTarget ? 'move' : 'cancel';
                }

                // If all dragged items are already in the target group, cancel the operation.
                if (
                    draggedItems.every(
                        (item) =>
                            (item.object === 'site-section' ||
                                item.object === 'site-external-link') &&
                            item.sectionGroup === targetItem.id
                    )
                ) {
                    return 'cancel';
                }

                return 'move';
            }

            const singleDraggedItem = draggedItems.length === 1 ? draggedItems[0] : null;

            // We can only move one item at a time.
            // It could be possible but we want to ship fast, so, let's keep it simple.
            if (!singleDraggedItem) {
                return 'cancel';
            }

            // Determine the rules based on the target item type.
            switch (targetItem.object) {
                case 'site-section-group': {
                    break;
                }
                case 'site-section': {
                    // If section is part of a group.
                    if (checkIsSectionInGroup(targetItem)) {
                        // We can only sections/groups before and after.
                        if (
                            singleDraggedItem.object !== 'site-section' &&
                            singleDraggedItem.object !== 'site-section-group' &&
                            singleDraggedItem.object !== 'site-external-link'
                        ) {
                            return 'cancel';
                        }
                    }

                    // Allow to move if the section are in different groups / levels.
                    if (
                        singleDraggedItem.object === 'site-section' &&
                        targetItem.sectionGroup !== singleDraggedItem.sectionGroup
                    ) {
                        return 'move';
                    }

                    break;
                }
                case 'site-external-link': {
                    if (singleDraggedItem.object === 'site-space') {
                        return 'cancel';
                    }
                    break;
                }
                case 'site-space': {
                    // We can only move sections and groups before and after first-level sections.
                    if (singleDraggedItem.object !== 'site-space') {
                        return 'cancel';
                    }

                    // We can only move sections and groups before and after first-level sections.
                    if (targetItem.section !== singleDraggedItem.section) {
                        return 'cancel';
                    }
                    break;
                }
                default:
                    assertNever(targetItem);
            }

            if (
                singleDraggedItem.object === 'site-external-link' &&
                targetItem.object !== 'site-space' &&
                (singleDraggedItem.sectionGroup ?? null) !== (targetItem.sectionGroup ?? null)
            ) {
                return 'move';
            }

            const targetIndex = getItemIndex(target.key);
            const draggedIndex = getItemIndex(singleDraggedItem.id);
            const resultIndex =
                target.dropPosition === 'before' ? targetIndex - 1 : targetIndex + 1;

            if (targetIndex === draggedIndex || resultIndex === draggedIndex) {
                return 'cancel';
            }

            return 'move';
        },
        onItemDrop: async (event) => {
            if (!event.isInternal) {
                return;
            }

            if (event.dropOperation !== 'move') {
                return;
            }

            const targetItem = getItem(event.target.key);

            // We only support site section groups as targets.
            if (targetItem?.object !== 'site-section-group') {
                return;
            }

            const keys = (
                await Promise.all(
                    event.items.map(async (item) => {
                        if (item.kind !== 'text') {
                            return null;
                        }
                        // We only support site sections.
                        if (!item.types.has('x-gitbook/site-section')) {
                            return null;
                        }
                        const text = await item.getText('x-gitbook/site-section');
                        return text;
                    })
                )
            ).filter((x) => x !== null);

            const siteSections = keys
                .map((key) => getItem(key))
                .filter((x) => x?.object === 'site-section');

            if (siteSections.length === 0) {
                const sectionGroupKeys = (
                    await Promise.all(
                        event.items.map(async (item) => {
                            if (
                                item.kind !== 'text' ||
                                !item.types.has('x-gitbook/site-section-group')
                            ) {
                                return null;
                            }
                            return item.getText('x-gitbook/site-section-group');
                        })
                    )
                ).filter((x) => x !== null);
                const siteSectionGroup = sectionGroupKeys
                    .map((key) => getItem(key))
                    .find((item) => item?.object === 'site-section-group');
                if (siteSectionGroup?.object === 'site-section-group') {
                    await moveSiteSectionGroup({
                        siteSectionGroup,
                        organization,
                        site,
                        parent: targetItem.id,
                    });
                    return;
                }

                const externalLinkKeys = (
                    await Promise.all(
                        event.items.map(async (item) => {
                            if (
                                item.kind !== 'text' ||
                                !item.types.has('x-gitbook/site-external-link')
                            ) {
                                return null;
                            }
                            return item.getText('x-gitbook/site-external-link');
                        })
                    )
                ).filter((key) => key !== null);
                const siteExternalLink = externalLinkKeys
                    .map((key) => getItem(key))
                    .find((item) => item?.object === 'site-external-link');
                if (siteExternalLink?.object === 'site-external-link') {
                    await moveSiteExternalLink({
                        siteExternalLink,
                        organization,
                        site,
                        parent: targetItem.id,
                    });
                }
                return;
            }

            addSectionsToGroup({
                sectionGroup: targetItem,
                siteSections,
                organization,
                site,
            });
        },
        onReorder: (event) => {
            // We only support move operations
            if (event.dropOperation !== 'move') {
                return;
            }

            // Handled by onItemDrop
            if (event.target.dropPosition === 'on') {
                return;
            }

            const targetItem = getItem(event.target.key);

            // No valid target item, bail.
            if (!targetItem) {
                return;
            }

            const movedItems = Array.from(event.keys)
                .map((key) => getItem(key))
                .filter((x) => x !== null);

            // No valid items moved, bail.
            if (movedItems.length === 0) {
                return;
            }

            const movedItem = movedItems.length === 1 ? movedItems[0] : null;

            // We only support moving one item at a time.
            if (!movedItem) {
                return;
            }

            onReorder({
                movedItem,
                targetItem,
                position: event.target.dropPosition,
            });
        },
    });
}
